import React, { useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DetailsCard from "../../../../../customComponents/DetailsCard";
import { useGetSharedNotesQuery, useGetSingleNoteQuery } from "../../../../../app/notesApiSlice";
import NotePreviewContent from "./NotePreviewContent";
import NoteComments from "./NoteComments";

const ViewNote = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const commentsRef = useRef(null);

  const noteFromState = location.state?.note;
  const { data: noteData, isLoading: isLoadingNote } = useGetSingleNoteQuery(
    { note_id: id },
    { skip: !id }
  );
  const { data: sharedData, isLoading: isLoadingShared } = useGetSharedNotesQuery();

  const note = noteData?.note || noteFromState || null;
  const sharedNote = useMemo(() => {
    if (!sharedData?.notes || !note) return null;
    return sharedData.notes.find((shared) => String(shared.note?.id) === String(note.id));
  }, [sharedData, note]);

  const loading = isLoadingNote || isLoadingShared;

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "—";
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const scrollToComments = () => {
    commentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!note) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          Note not found. Please return to the note list.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            size="small"
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {note.topic}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/lms/notes/share-note/${note.id}`, { state: { note } })}
          >
            Share
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/lms/notes/new-note`, { state: { cardData: note } })}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={scrollToComments}
            disabled={!sharedNote}
          >
            View Comments
          </Button>
        </Stack>
      </Stack>

      <DetailsCard
        header={note.topic}
        subHeading1="Subject"
        value1={note.subject || "—"}
        subHeading2="Level"
        value2={note.level || "—"}
        subHeading3="Updated"
        value3={formatDate(note.updated_at)}
      />

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Note content
          </Typography>
          <NotePreviewContent note={note} />
        </CardContent>
      </Card>

      <Box ref={commentsRef}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Comments
        </Typography>
        <NoteComments
          sharedNote={sharedNote}
          emptyMessage="No comments have been submitted yet."
        />
      </Box>
    </Box>
  );
};

export default ViewNote;
